import React from 'react';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import {
  ReceiptPercentIcon,
  ClockIcon,
  UserCircleIcon,
  PhoneIcon,
  EnvelopeIcon,
  HashtagIcon,
} from '@heroicons/react/24/outline';
import { PaymentStatus } from '../../../../data/enums';
import { formatPhoneNumber } from '../../../../utils/baseUtils';
import { GOOGLE_ADS_SOURCE } from '../../../integrations/google-ads/google-ads-settings';

const cn = (...c) => c.filter(Boolean).join(' ');

const paymentStatusBadge = (status) => {
  switch (status) {
    case 0:
      return 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300';
    case 20:
      return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
    default:
      return 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300';
  }
};

const Card = ({ children, className }) => (
  <div
    className={cn(
      'reveal order-card overflow-hidden rounded-md border border-slate-200 shadow-sm dark:border-slate-700',
      className,
    )}
  >
    {children}
  </div>
);

const CardHeader = ({ icon: Icon, label, accent = false }) => (
  <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-3 dark:border-slate-700/60">
    <div
      className={cn(
        'flex h-7 w-7 items-center justify-center rounded-md',
        accent
          ? 'bg-blue-500/10 text-blue-600 dark:bg-sky-400/10 dark:text-sky-400'
          : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400',
      )}
    >
      <Icon className="h-4 w-4" />
    </div>
    <h3 className="text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
      {label}
    </h3>
  </div>
);

const SummaryCards = ({ orderDetail, publicReceipt = false }) => {
  const createdLabel = orderDetail.store?.timezoneid
    ? dayjs(orderDetail.createdate)
        .tz(orderDetail.store.timezoneid)
        .format('MM/DD/YYYY hh:mm A')
    : dayjs(orderDetail.createdate)
        .utc()
        .utcOffset((orderDetail.timezone || 0) * 60)
        .format('MM/DD/YYYY hh:mm A');

  const customerName =
    orderDetail.name || orderDetail.customer?.fullname || '—';
  const customerPhone = orderDetail.phone || orderDetail.customer?.phone;
  const customerEmail = orderDetail.email || orderDetail.customer?.email;
  const orderTypeLabel = orderDetail.ordertype === 10 ? 'Dine-In' : 'To Go';

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader
          icon={ReceiptPercentIcon}
          label="Order Type"
          accent
        />
        <div className="flex items-center justify-between px-5 py-5">
          <div>
            <p className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
              {orderTypeLabel}
            </p>
            {orderDetail.table && (
              <p className="mt-0.5 text-sm font-medium text-slate-500 dark:text-slate-400">
                Table {orderDetail.table}
              </p>
            )}
            {orderDetail.marketingsource === GOOGLE_ADS_SOURCE ? (
              <p className="mt-2 text-xs font-semibold text-sky-700 dark:text-sky-300">
                Source: Google Ads
              </p>
            ) : null}
          </div>
          <div className="text-right">
            <p className="text-[0.62rem] font-semibold uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">
              Receipt #
            </p>
            <p className="mt-0.5 tabular-nums text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
              {orderDetail.ordernumberstr || '—'}
            </p>
          </div>
        </div>
      </Card>

      <Card>
        <CardHeader icon={HashtagIcon} label="Order Details" />
        <div className="flrounded-mdenter justify-between gap-4 px-5 py-5">
          <div className="min-w-0">
            <Tooltip
              id="order-created-tooltip"
              place="top"
              className="!rounded-lg !bg-blue-600 !px-3 !py-2 !text-sm !font-medium !text-white"
            />
            <p className="text-base font-semibold text-slate-900 dark:text-slate-100">
              Order #{orderDetail.orderid}
            </p>
            <div
              className="mt-2 flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400"
              data-tooltip-id="order-created-tooltip"
              data-tooltip-content="Order created"
            >
              <ClockIcon className="h-4 w-4 text-blue-500 dark:text-sky-400" />
              <span className="tabular-nums">{createdLabel}</span>
            </div>
          </div>
          <span
            className={cn(
              paymentStatusBadge(orderDetail.paymentstatus),
              'inline-flex flex-shrink-0 items-center rounded-full px-3 py-1 text-xs font-semibold',
            )}
          >
            {PaymentStatus[orderDetail.paymentstatus]}
          </span>
        </div>
      </Card>

      <div className="lg:col-span-2">
        <Card>
          <CardHeader icon={UserCircleIcon} label="Customer" />
          <div className="flex flex-wrap items-center gap-x-8 gap-y-3 px-5 py-5">
            <div className="flex items-center gap-2">
              <UserCircleIcon className="h-5 w-5 text-slate-400" />
              <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                {customerName}
              </span>
            </div>
            {customerPhone && (
              <div className="flex items-center gap-2">
                <PhoneIcon className="h-5 w-5 text-slate-400" />
                {publicReceipt ? (
                  <span className="text-sm font-semibold tabular-nums text-slate-900 dark:text-slate-100">
                    {formatPhoneNumber(customerPhone)}
                  </span>
                ) : (
                  <Link
                    to={`/dashboard/customers/${orderDetail.customerid}/orders`}
                    className="text-sm font-semibold tabular-nums text-blue-600 hover:underline dark:text-sky-400"
                  >
                    {formatPhoneNumber(customerPhone)}
                  </Link>
                )}
              </div>
            )}
            {customerEmail && (
              <div className="flex items-center gap-2">
                <EnvelopeIcon className="h-5 w-5 text-slate-400" />
                <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  {customerEmail}
                </span>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default SummaryCards;
